import axios, { AxiosError, AxiosInstance } from 'axios';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000/v1';
export const API_BASE_URL = baseURL;

const STORAGE_KEY = 'courier-access-token';

class TokenStore {
  set(t: string | null) {
    if (t) localStorage.setItem(STORAGE_KEY, t);
    else localStorage.removeItem(STORAGE_KEY);
  }
  get(): string | null {
    return localStorage.getItem(STORAGE_KEY);
  }
}

export const tokenStore = new TokenStore();

// Download an authenticated endpoint as a file through `api`, so the 401 -> refresh -> retry
// interceptor applies (fixes CSV exports failing with Unauthorized on a stale access token)
// and the JWT never appears in the URL. `path` is relative to the API base.
/**
 * Pull the REAL message out of a failed blob download.
 *
 * With `responseType: 'blob'` axios hands back the error body as a Blob, so the usual
 * `e.response.data.message` is `undefined` and every failure collapses into whatever generic string
 * the caller passes as a fallback. That is how a precise, actionable refusal — "You don't have
 * permission to export B2B orders. Ask an administrator to grant Orders export under Users → Roles
 * & Permissions." — reached the user as "Export failed", leaving them with nothing to act on and
 * nothing for support to go on either.
 *
 * A timeout has no response at all, and is called out by name: it is the most common export failure
 * and the only one where the fix is "narrow the filters", not "ask for a permission".
 */
export async function blobErrorMessage(e: any, fallback = 'Download failed'): Promise<string> {
  if (e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message ?? ''))) {
    return 'The export took too long and timed out. Narrow the date range or filters and try again.';
  }
  const data = e?.response?.data;
  try {
    const text = data instanceof Blob ? await data.text() : typeof data === 'string' ? data : null;
    if (text) {
      try { return JSON.parse(text)?.message ?? text.slice(0, 300); } catch { return text.slice(0, 300); }
    }
    if (data?.message) return data.message;
  } catch { /* fall through to the fallback */ }
  return e?.message ?? fallback;
}

export async function downloadBlob(path: string, filename: string, config?: any) {
  // Exports now return the FULL filtered set rather than the first 10k/500/200 rows, so a wide
  // export is minutes of server work — well past the client's 30s default, at which axios would
  // throw the finished file away and report a failure. Same trap the bulk-ship flow hit.
  const res = await api.get(path, { timeout: 15 * 60_000, ...(config ?? {}), responseType: 'blob' });
  // Some servers send CSV/XLSX with a generic content-type; force a Blob so the
  // browser treats it as a downloadable file rather than trying to render it.
  const data = res.data instanceof Blob ? res.data : new Blob([res.data as any]);
  const url = URL.createObjectURL(data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next tick — revoking synchronously right after click() can
  // abort the download in Chrome before it starts (the file silently never saves).
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// ─────────────────────────── R2 object storage helpers ───────────────────────────
export type UploadCategory =
  | 'b2b_pod' | 'b2c_pod' | 'employee_docs' | 'merchant_kyc' | 'merchant_logo'
  | 'courier_logo'
  | 'product_image' | 'invoice' | 'attendance_selfie' | 'signature' | 'misc';

/** Upload a file to R2 (private) via the backend. Persist `ref`; use `url` for an immediate preview. */
export async function uploadFile(file: File | Blob, category: UploadCategory, filename?: string): Promise<{ ref: string; url: string; mimetype: string; sizeBytes: number }> {
  const fd = new FormData();
  fd.append('file', file, filename ?? (file as File).name ?? 'upload');
  const res = await api.post(`/uploads?category=${encodeURIComponent(category)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
}

/** True if a stored value is an R2 key that must be signed before display (vs a data:/http URL). */
export function isStorageRef(v?: string | null): boolean {
  return !!v && !v.startsWith('data:') && !v.startsWith('http://') && !v.startsWith('https://') && v.includes('/');
}

/** Resolve a stored reference to a viewable URL. data:/http values pass through unchanged. */
export async function signFileUrl(ref?: string | null): Promise<string> {
  if (!ref) return '';
  if (!isStorageRef(ref)) return ref;
  const res = await api.get('/files/sign', { params: { ref } });
  return res.data?.url ?? ref;
}

export const api: AxiosInstance = axios.create({
  baseURL,
  withCredentials: true,
  timeout: 30_000,
});

/**
 * The warehouse + merchant currently drilled into under WMS.
 *
 * Held here rather than passed down because ~20 WMS screens each build their own query, and adding
 * the pair to every one of them by hand is exactly how two tabs end up disagreeing about whose stock
 * they are showing. `WmsRoutes` sets it from the URL and clears it on the way out.
 */
export const wmsScope: { warehouseId: number | null; merchantId: number | null } = { warehouseId: null, merchantId: null };

api.interceptors.request.use((cfg) => {
  const token = tokenStore.get();
  if (token) cfg.headers.Authorization = `Bearer ${token}`;

  // Scope every WMS READ to the drilled-into warehouse + merchant.
  //
  // GET only, and never overwriting a value the caller set: a screen that deliberately asks about
  // another warehouse (a stock TRANSFER names two of them) must keep its own parameters. The
  // warehouse/merchant master lists are exempt for the obvious reason — they are what the pickers
  // read to offer the choice in the first place.
  const url = String(cfg.url ?? '');
  const isWmsRead = (cfg.method ?? 'get').toLowerCase() === 'get' && /(^|\/)wms\//.test(url);
  const isPickerList = /\/wms\/(warehouses|merchants)(\?|$|\/)/.test(url);
  if (isWmsRead && !isPickerList && (wmsScope.warehouseId || wmsScope.merchantId)) {
    const params = { ...(cfg.params ?? {}) } as Record<string, any>;
    if (wmsScope.warehouseId && params.warehouseId === undefined) params.warehouseId = wmsScope.warehouseId;
    if (wmsScope.merchantId && params.merchantId === undefined) params.merchantId = wmsScope.merchantId;
    cfg.params = params;
  }
  return cfg;
});

let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const original = error.config as any;
    if (error.response?.status === 401 && !original?._retry && !original?.url?.includes('/auth/')) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${baseURL}/auth/refresh`, {}, { withCredentials: true })
            .then((r) => {
              const t = r.data.accessToken as string;
              tokenStore.set(t);
              return t;
            })
            .catch(() => null)   // Do NOT clear token on refresh failure — keep user logged in
            .finally(() => { refreshPromise = null; });
        }
        const newToken = await refreshPromise;
        if (newToken) {
          original.headers.Authorization = `Bearer ${newToken}`;
          return api(original);
        }
        // Refresh failed → token has truly expired. Clear token + redirect to login.
        tokenStore.set(null);
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/signup')) {
          window.location.href = '/login';
        }
      } catch {
        tokenStore.set(null);
        if (!window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/signup')) {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  },
);
